import {
  createContext,
  useContext,
  useEffect,
  useState,
  type ReactNode,
} from 'react';

const UI_FONT_SCALE_STORAGE_KEY = 'accounts.ui.fontScale';

export type UiFontScale = 'standard' | 'large' | 'extra-large';

export const UI_FONT_SCALE_OPTIONS: {
  value: UiFontScale;
  label: string;
  note: string;
}[] = [
  { value: 'standard', label: 'Compact', note: 'Smallest — most data per screen.' },
  { value: 'large', label: 'Medium', note: 'A step down from the default.' },
  { value: 'extra-large', label: 'Comfortable', note: 'Largest, most readable — the default.' },
];

interface UiPreferencesContextValue {
  readonly fontScale: UiFontScale;
  readonly setFontScale: (scale: UiFontScale) => void;
}

const UiPreferencesContext = createContext<UiPreferencesContextValue | null>(null);

export function normalizeUiFontScale(raw: unknown): UiFontScale {
  if (raw === 'standard' || raw === 'large' || raw === 'extra-large') return raw;
  // Default = the largest (comfortable) size; two smaller presets opt-in below it.
  return 'extra-large';
}

function readStoredUiFontScale(): UiFontScale {
  if (typeof window === 'undefined') return 'standard';
  try {
    return normalizeUiFontScale(window.localStorage.getItem(UI_FONT_SCALE_STORAGE_KEY));
  } catch {
    return 'standard';
  }
}

function applyUiFontScale(scale: UiFontScale) {
  if (typeof document === 'undefined') return;
  document.documentElement.dataset.uiFontScale = scale;
}

export function primeUiFontScale() {
  applyUiFontScale(readStoredUiFontScale());
}

export function UiPreferencesProvider({ children }: { children: ReactNode }) {
  const [fontScale, setFontScale] = useState<UiFontScale>(() => readStoredUiFontScale());

  useEffect(() => {
    applyUiFontScale(fontScale);
    try {
      window.localStorage.setItem(UI_FONT_SCALE_STORAGE_KEY, fontScale);
    } catch {
      // Ignore storage failures so the live app keeps rendering.
    }
  }, [fontScale]);

  useEffect(() => {
    const onStorage = (event: StorageEvent) => {
      if (event.key !== UI_FONT_SCALE_STORAGE_KEY) return;
      setFontScale(normalizeUiFontScale(event.newValue));
    };

    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, []);

  return (
    <UiPreferencesContext.Provider value={{ fontScale, setFontScale }}>
      {children}
    </UiPreferencesContext.Provider>
  );
}

export function useUiPreferences() {
  const value = useContext(UiPreferencesContext);
  if (!value) throw new Error('useUiPreferences must be used within UiPreferencesProvider');
  return value;
}
